import i18nFactory from '@yandex-int/i18n';
import React from 'react';
import { AlertOptions } from 'react-s-alert';

import { NoticeType, showNoticeMessage } from '../../actions/grid-screen';
import { AlertType } from '../../components/Alert/Alert';
import { ButtonDecorator } from '../../components/ButtonDecorator/ButtonDecorator';
import {
    banners,
    campaigns,
    CellKeyFields,
    ColumnTypesSettings,
    feedFilters,
    groups,
    LinkTarget,
    phrases,
    retargetings,
    usersProfile
} from '../../typings/common';
import {
    BannersAdditionalFilter,
    CampaignsAdditionalFilter,
    CampaignsCalcTypeFilter,
    GridRow,
    GroupsAdditionalFilter,
    ScreenAdditionalFilter
} from '../../typings/domain';
import {
    GdAdGroupOrderByFieldInput,
    GdAdOrderByFieldInput,
    GdCampaignOrderByFieldInput,
    GdRetargetingOrderByFieldInput,
    GdShowConditionOrderByFieldInput,
    GdSmartFilterOrderByFieldInput,
    OrderInput
} from '../../typings/graphql-grid';
import {
    GridAlertMessageType,
    GridCacheKeys,
    GridColumnDecl,
    GridContextTitleData,
    GridEditCells,
    GridEditCellStatus,
    GridInitialData,
    GridScreenAdditionalFilterItem,
    GridTotalStatsInfo,
    GridViewType
} from '../../typings/grid';
import { Session } from '../Session/Session';

import * as keyset from './GridHelpers.i18n';

const i18n = i18nFactory(keyset);

export function getTitleDataForView(
    view: GridViewType,
    additionalFilter?: ScreenAdditionalFilter
): GridContextTitleData {
    if (view === campaigns) {
        return {
            text: i18n('all-campaigns')
        };
    }

    // для каждого типа фильтра архивности нужно показывать свои тайтлы
    // По умолчанию показываем активные вещи
    switch (view) {
        case groups:
            if (additionalFilter === GroupsAdditionalFilter.ALL) {
                return {
                    text: i18n('all-groups')
                };
            }
            if (additionalFilter === GroupsAdditionalFilter.ALL_ARCHIVED) {
                return {
                    text: i18n('archive-groups')
                };
            }

            return {
                text: i18n('active-groups')
            };

        case banners:
            if (additionalFilter === BannersAdditionalFilter.ALL) {
                return {
                    text: i18n('all-banners')
                };
            }
            if (additionalFilter === BannersAdditionalFilter.ALL_ARCHIVED) {
                return {
                    text: i18n('archive-banners')
                };
            }
            return {
                text: i18n('active-banners')
            };
        case phrases:
            return {
                text: i18n('all-phrases')
            };

        case retargetings:
        case usersProfile:
            return {
                text: i18n('all-targetings')
            };

        default:
            throw Error(
                `Невозможно сформировать данные для заголовка. Неподдерживаемый view ${view}`
            );
    }
}

export function getCampaignsTitleData(
    additionalFilter?: ScreenAdditionalFilter
): { text: string } {
    switch (additionalFilter) {
        case CampaignsAdditionalFilter.ALL_ARCHIVED:
            return {
                text: i18n('archive-campaigns')
            };
        case CampaignsAdditionalFilter.ALL:
            return {
                text: i18n('all-campaigns')
            };
        default:
            return {
                text: i18n('active-campaigns')
            };
    }
}

export function getDefaultAdditionalFilterValue(
    view: GridViewType
):
    | CampaignsAdditionalFilter
    | GroupsAdditionalFilter
    | BannersAdditionalFilter {
    switch (view) {
        case campaigns:
            return CampaignsAdditionalFilter.ALL_ACTIVE;
        case groups:
            return GroupsAdditionalFilter.ALL_ACTIVE;
        case banners:
            return BannersAdditionalFilter.ALL_ACTIVE;
        default:
            return CampaignsAdditionalFilter.ALL_ACTIVE;
    }
}

export function getArchiveFilterBadgeType(
    additionalFilter?: ScreenAdditionalFilter
): string {
    switch (additionalFilter) {
        case CampaignsAdditionalFilter.ALL_ARCHIVED:
        case GroupsAdditionalFilter.ALL_ARCHIVED:
        case BannersAdditionalFilter.ALL_ARCHIVED:
            return 'archived';
        case CampaignsAdditionalFilter.ALL:
        case GroupsAdditionalFilter.ALL:
        case BannersAdditionalFilter.ALL:
            return 'all';
        default:
            return 'active';
    }
}

export function getArchiveFilterBadgeText(
    additionalFilter?: ScreenAdditionalFilter
): string {
    switch (additionalFilter) {
        case CampaignsAdditionalFilter.ALL:
            return i18n('all-campaigns');
        case CampaignsAdditionalFilter.ALL_ACTIVE:
            return i18n('active-campaigns');
        case CampaignsAdditionalFilter.ALL_ARCHIVED:
            return i18n('archive-campaigns');
        case GroupsAdditionalFilter.ALL:
            return i18n('all-groups');
        case GroupsAdditionalFilter.ALL_ACTIVE:
            return i18n('active-groups');
        case GroupsAdditionalFilter.ALL_ARCHIVED:
            return i18n('archive-groups');
        case BannersAdditionalFilter.ALL:
            return i18n('all-banners');
        case BannersAdditionalFilter.ALL_ACTIVE:
            return i18n('active-banners');
        case BannersAdditionalFilter.ALL_ARCHIVED:
            return i18n('archive-banners');
        default:
            return i18n('active-campaigns');
    }
}

export function getCampaignsAdditionalFilterBadgeText(
    additionalFilter?: ScreenAdditionalFilter,
    calcTypeFilter?: CampaignsCalcTypeFilter
): string {
    const isCPC = calcTypeFilter === CampaignsCalcTypeFilter.CPC;

    switch (additionalFilter) {
        case CampaignsAdditionalFilter.ALL:
            return isCPC
                ? i18n('all-cpc-campaigns')
                : i18n('all-cpm-campaigns');
        case CampaignsAdditionalFilter.ALL_ARCHIVED:
            return isCPC
                ? i18n('archive-cpc-campaigns')
                : i18n('archive-cpm-campaigns');
        case CampaignsAdditionalFilter.ALL_ACTIVE:
        default:
            return isCPC
                ? i18n('active-cpc-campaigns')
                : i18n('active-cpm-campaigns');
    }
}

export function getAdditionalFilterItems(
    view: GridViewType
): Array<GridScreenAdditionalFilterItem> {
    switch (view) {
        case campaigns:
            return [
                {
                    text: i18n('all-campaigns'),
                    val: CampaignsAdditionalFilter.ALL
                },
                {
                    text: i18n('active-campaigns'),
                    val: CampaignsAdditionalFilter.ALL_ACTIVE
                },
                {
                    text: i18n('archive-campaigns'),
                    val: CampaignsAdditionalFilter.ALL_ARCHIVED
                }
            ];
        case groups:
            return [
                {
                    text: i18n('all-groups'),
                    val: GroupsAdditionalFilter.ALL
                },
                {
                    text: i18n('all-active-groups'),
                    val: GroupsAdditionalFilter.ALL_ACTIVE
                },
                {
                    text: i18n('all-archived-groups'),
                    val: GroupsAdditionalFilter.ALL_ARCHIVED
                }
            ];
        case banners:
            return [
                {
                    text: i18n('all-banners'),
                    val: BannersAdditionalFilter.ALL
                },
                {
                    text: i18n('active-banners'),
                    val: BannersAdditionalFilter.ALL_ACTIVE
                },
                {
                    text: i18n('archive-banners'),
                    val: BannersAdditionalFilter.ALL_ARCHIVED
                }
            ];
        default:
            return [];
    }
}

export function getCalcTypeFilterItems(
    view: GridViewType,
    isCpmCampaignsEnabled?: boolean
): Array<GridScreenAdditionalFilterItem> {
    /*
    Для того, чтобы значение быстрофильтра передавалось
    на грид фраз при формировании запроса и корректного
    применения фильтра (view === phrases). DIRECT-97218
     */
    return (view === campaigns || view === phrases) && isCpmCampaignsEnabled
        ? [
              {
                  text: i18n('cpm-campaigns'),
                  val: CampaignsCalcTypeFilter.CPM
              },
              {
                  text: i18n('cpc-campaigns'),
                  val: CampaignsCalcTypeFilter.CPC
              }
          ]
        : [];
}

export function normalizeIds(
    idsParam: string | null
): Array<string> | undefined {
    return idsParam ? idsParam.split(',') : undefined;
}

export const getEmptyCacheKeys = (): GridCacheKeys => {
    return {
        campaigns: '',
        groups: '',
        banners: '',
        showConditions: ''
    };
};

export const getEditCellKey = ({ id, column, view }: CellKeyFields): string => {
    return id + column + view;
};

export const getEditCellData = (
    stateEditCellData: GridEditCells,
    cellId: string,
    cellName: string,
    view: GridViewType
): { isSaving: boolean; error?: string } => {
    const key = getEditCellKey({
        id: cellId,
        column: cellName,
        view
    });

    if (!stateEditCellData[key]) {
        return {
            isSaving: false
        };
    }

    return {
        isSaving: stateEditCellData[key].isSaving,
        error: stateEditCellData[key].error as string
    };
};

export const deleteEditCellData = (
    cellKeyFields: CellKeyFields,
    cells: GridEditCells
): GridEditCells => {
    const cellKey = getEditCellKey(cellKeyFields);

    delete cells[cellKey];

    return cells;
};

export const updateEditCellData = (
    cellKeyFields: CellKeyFields,
    cells: GridEditCells,
    data: GridEditCellStatus
): GridEditCells => {
    const cellKey = getEditCellKey(cellKeyFields);

    cells[cellKey] = data;

    return cells;
};

export const getHintForTabMenuLink = (
    selectedItemsCount: number
): string | undefined => {
    return selectedItemsCount > 0 ? String(selectedItemsCount) : undefined;
};

export const getDefaultSort = (
    view: GridViewType
): Array<{
    order: OrderInput;
    field:
        | GdCampaignOrderByFieldInput
        | GdAdGroupOrderByFieldInput
        | GdAdOrderByFieldInput
        | GdShowConditionOrderByFieldInput
        | GdRetargetingOrderByFieldInput
        | GdSmartFilterOrderByFieldInput;
}> => {
    switch (view) {
        case campaigns:
            return [
                {
                    order: OrderInput.ASC,
                    field: GdCampaignOrderByFieldInput.NAME
                }
            ];
        case groups:
            return [
                {
                    order: OrderInput.DESC,
                    field: GdAdGroupOrderByFieldInput.CAMPAIGN_ID
                },
                {
                    order: OrderInput.DESC,
                    field: GdAdGroupOrderByFieldInput.ID
                }
            ];
        case banners:
            return [
                {
                    order: OrderInput.DESC,
                    field: GdAdOrderByFieldInput.GROUP_ID
                },
                {
                    order: OrderInput.DESC,
                    field: GdAdOrderByFieldInput.ID
                }
            ];
        case phrases:
            return [
                {
                    order: OrderInput.DESC,
                    field: GdShowConditionOrderByFieldInput.GROUP_ID
                },
                {
                    order: OrderInput.DESC,
                    field: GdShowConditionOrderByFieldInput.ID
                }
            ];
        case retargetings:
            return [
                {
                    order: OrderInput.ASC,
                    field: GdRetargetingOrderByFieldInput.NAME
                }
            ];
        case usersProfile:
            return [
                {
                    order: OrderInput.ASC,
                    field: GdRetargetingOrderByFieldInput.NAME
                }
            ];
        case feedFilters:
            return [
                {
                    order: OrderInput.DESC,
                    field: GdSmartFilterOrderByFieldInput.GROUP_ID
                },
                {
                    order: OrderInput.DESC,
                    field: GdSmartFilterOrderByFieldInput.ID
                }
            ];
        default:
            return [];
    }
};

export const isStatusTextVisible = (
    isCompactViewEnabled: boolean,
    columnViewInfo: ColumnTypesSettings
): boolean => {
    const { width, widthCompact } = columnViewInfo;

    return isCompactViewEnabled
        ? Boolean(widthCompact && widthCompact > 100)
        : width > 100;
};

export const getBannerColumnHeight = (
    columns: Array<GridColumnDecl<GridRow>>,
    columnKey: string,
    isCompactViewEnabled: boolean
): number => {
    const bannerColumnVisible = columns.some(({ key }) => key === columnKey);

    if (bannerColumnVisible) {
        return isCompactViewEnabled ? 145 : 175;
    }

    return isCompactViewEnabled ? 50 : 87;
};

export function getGridAlertMessage(
    errorType: GridAlertMessageType
): string | undefined {
    switch (errorType) {
        case GridAlertMessageType.minusPhraseUnbindingError:
            return i18n('minus-phrase-unbinding-error-grid-alert-message');
        case GridAlertMessageType.minusPhraseBindingError:
            return i18n('minus-phrase-binding-error-grid-alert-message');
        default:
            return undefined;
    }
}

export function getGridAlertMessageType(
    errorType: GridAlertMessageType
): AlertType {
    switch (errorType) {
        case GridAlertMessageType.minusPhraseUnbindingError:
        case GridAlertMessageType.minusPhraseBindingError:
            return AlertType.error;
        default:
            return AlertType.info;
    }
}

/* Выводит нотификационное сообщения с кнопкой */
export function showNoticeMessageWithLink(
    type: NoticeType,
    message: string,
    link: string,
    linkMessage: string,
    options: AlertOptions = {}
): void {
    const button = (
        <ButtonDecorator
            size="s"
            view="classic"
            theme="normal"
            text={linkMessage}
            type="link"
            url={link}
            target={LinkTarget.blank}
        />
    );

    showNoticeMessage(type, message, {
        ...options,
        customFields: {
            ...options.customFields,
            button
        }
    });
}

export function getGridTotalStatsKeys(): Array<keyof GridTotalStatsInfo> {
    return ['cost', 'shows', 'clicks', 'ctr'];
}

export function prepareGridDataStatsInfo(
    newStatsInfo?: GridTotalStatsInfo,
    currentStatsInfo?: GridTotalStatsInfo
): GridTotalStatsInfo | undefined {
    let totalStats: GridTotalStatsInfo | undefined;

    if (newStatsInfo && currentStatsInfo) {
        totalStats = ['cost', 'shows', 'clicks'].reduce((stats, prop) => {
            const currentValue = currentStatsInfo[prop];
            const newValue = newStatsInfo[prop];
            const useNew =
                newValue !== undefined &&
                Number(newValue) > Number(currentValue);

            stats[prop] = useNew ? newValue : currentValue;

            return stats;
        }, {});
    } else if (!newStatsInfo) {
        totalStats = currentStatsInfo;
    } else {
        totalStats = newStatsInfo;
    }

    if (!totalStats) {
        return;
    }

    const { shows, clicks } = totalStats;

    const clicksCount = Number(clicks);
    const showsCount = Number(shows);

    let ctr = 0;

    if (clicksCount && showsCount) {
        ctr = clicksCount / showsCount;
    }

    return {
        ...totalStats,
        ctr: (100 * ctr).toFixed(ctr ? 2 : 0)
    };
}

export function getGridInitialData(login: string): GridInitialData {
    const {
        gridFilter = {},
        campaignsCalcTypeFilter = CampaignsCalcTypeFilter.CPC
    } = Session.getValueFromCurrentSession(login);
    const { goalsSettings = [] } = Session.getValueFromCurrentLocalSession(
        login
    );

    return {
        filterDict: gridFilter,
        campaignsCalcTypeFilter,
        goalsSettings
    };
}
